import { HeadersOptionsType } from "@/types/ModuleOptionsType";
import { ParentsType } from "../../../wp/getParentPages";

import { useSublinesVisibility } from "../../../hooks/useSublinesOverlinesVisibility";
import HeaderBase from "@/components/_headers/HeaderBase/HeaderBase";
import { MediaItem } from "@/components/Global/MediaItem/MediaItem";
import { Headline } from "@/components/Global/Headline/Headline";
import Split from "@/components/_primitives/Split/Split";
import { bulmaColumnsToWidth, columnSize } from "@/utils/layout/columnSize";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Post } from "@/wp/types/client";
import { ImageType } from "@/types/baseTypes";
import { getCategories } from "@/wp/getCategory";
import { useState, useEffect } from "react";

export interface PostHeaderProps {
  post: Post<"blog">;
  headerOptions?: HeadersOptionsType["header_posts"];
  parents: ParentsType;
  fallbackImg?: ImageType | false;
}

export const PostHeader = ({
  post,
  headerOptions,
  parents,
  fallbackImg,
}: PostHeaderProps) => {
  const header = post.acf.header_post;
  const {
    overline = "",
    subline = "",
    media,
    type,
    background_color_class,
    section_margin_class,
    headline_text_align_class,
    row_direction_class,
    content,
    section_padding_class,
  } = header;

  const {
    show_overline_subline_headers: show_overline_subline,
    textheader,
    imageheader,
    splitheader,
  } = headerOptions ?? {};

  const { text_width: textheaderTextWidth } = textheader ?? {};

  const {
    text_horizontal_align: imageheaderTextHorizontalAlign,
    text_vertical_align: imageheaderTextVerticalAlign,
    vertical_align: imageheaderVerticalAlign,
    headline_gradient: imageheaderHeadlineGradient,
    text_width: imageheaderTextWidth,
    image_height: imageheaderImageHeight,
  } = imageheader ?? {};

  const {
    image_height: splitheaderImageHeight,
    media_format: splitheaderMediaFormat,
    header_width: splitheaderWidth,
    text_vertical_align: splitheaderTextVerticalAlign,
    text_width_with_spacer: splitheaderTextWidthWithSpacer,
  } = splitheader ?? {};

  /* jscpd:ignore-start */
  const {
    showSubline,
    showOverline,
    showCategories,
    showDate,
    showDateAndCategories,
  } = useSublinesVisibility(show_overline_subline ?? "none");
  /* jscpd:ignore-end */
  const [postCategories, setPostCategories] = useState<string[]>([]);
  const date = new Date(post.date_gmt).toLocaleDateString("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  useEffect(() => {
    async function fetchCat() {
      const categories = await getCategories({ category: "categories" });
      setPostCategories(
        categories
          .filter((cat) => post?.categories?.includes(cat.id))
          .map((cat) => cat.name),
      );
    }
    fetchCat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overlineJSX = `${showDate || showDateAndCategories ? date : ""}${showDateAndCategories ? " | " : ""}${(showCategories || showDateAndCategories) && postCategories.length > 0 ? `${postCategories.join(" | ")}` : ""}`;
  const splitHeaderDirection =
    row_direction_class === "is-row-normal-desktop" ? "row-reverse" : "row";

  const breadcrumbsJSX = (
    <Breadcrumbs
      parents={parents}
      post={post}
      breadcrumbPositon={
        header.type === "splitheader" && splitHeaderDirection === "row"
          ? "is-justify-content-flex-end"
          : "is-justify-content-flex-start"
      }
    />
  );

  const mediaJSX = (
    <MediaItem
      image_desktop={
        media.image_desktop
          ? media.image_desktop
          : fallbackImg
            ? fallbackImg
            : false
      }
      image_tablet={media.image_tablet}
      image_mobile={media.image_mobile}
      image_fullhd={media.image_fullhd}
      // mediaFormat={imageheaderMediaFormat}
      sizes="(max-width: 767px) 50vw, 100vw"
      priority={true}
      mediaFormat={
        header.type === "splitheader" ? splitheaderMediaFormat : undefined
      }
      className={`${header.type === "splitheader" && splitheaderImageHeight === "image-height" ? "auto-height" : ""}`}
    />
  );

  const contentJSX = (
    <Headline
      headline={header.title}
      headline_html_tag="h1-posts"
      overline={
        showOverline
          ? overline
          : showDate || showCategories || showDateAndCategories
            ? overlineJSX
            : ""
      }
      subline={showSubline ? subline : ""}
      introText={content}
      textAlignClass={headline_text_align_class}
      disableAnimation
    />
  );

  switch (type) {
    case "textheader":
      return (
        <HeaderBase
          className={`${background_color_class || ""} ${section_margin_class} post-header`}
          header={breadcrumbsJSX}
          content={contentJSX}
          headerFrame={"frameless"}
          contentFrame={"narrow-frame"}
          contentWrapperClassName={`header-content ${section_padding_class}`}
          narrowFrameColumns={
            columnSize(textheaderTextWidth ?? false) || "is-12"
          }
          contentHorizontalAlignment="center"
          height={"content-height"}
        />
      );
    case "imageheader":
      return (
        <HeaderBase
          className={`${background_color_class || ""} ${section_margin_class}`}
          header={mediaJSX}
          content={contentJSX}
          headerFrame={"frameless"}
          contentFrame={"narrow-frame"}
          narrowFrameColumns={
            columnSize(imageheaderTextWidth ?? false) || "is-12"
          }
          contentHorizontalAlignment={imageheaderTextHorizontalAlign || "start"}
          contentVerticalAlignment={imageheaderTextVerticalAlign || "start"}
          height={
            imageheaderImageHeight === "screen-height"
              ? "screen-height"
              : "content-height"
          }
          heightDependency="content"
          arrangement={
            imageheaderVerticalAlign === "overlay"
              ? "overlay"
              : imageheaderVerticalAlign === "media-top-text-bottom"
                ? "content-bottom"
                : "content-top"
          }
          gradient={imageheaderHeadlineGradient ?? false}
        />
      );
    case "splitheader":
      return (
        <HeaderBase
          className={`${background_color_class || ""} ${section_margin_class}`}
          contentFrame={
            splitheaderImageHeight === "screen-height-frameless"
              ? "frameless"
              : "narrow-frame"
          }
          headerFrame={"frameless"}
          contentHorizontalAlignment="center"
          height={
            splitheaderImageHeight === "screen-height-frameless"
              ? "screen-height"
              : "content-height"
          }
          narrowFrameColumns={
            splitheaderImageHeight === "screen-height-frameless"
              ? "is-12"
              : columnSize(splitheaderWidth ?? false) || "is-12"
          }
          arrangement="content-bottom"
          header={
            splitheaderImageHeight === "screen-height-frameless"
              ? undefined
              : breadcrumbsJSX
          }
          content={
            <Split
              first={mediaJSX}
              firstClassName={`min-height-acceptor-desktop `}
              second={contentJSX}
              direction={splitHeaderDirection}
              firstVerticalAlignment={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "stretch"
                  : "start"
              }
              secondOuterFractional={
                splitheaderImageHeight === "screen-height-frameless"
                  ? breadcrumbsJSX
                  : undefined
              }
              secondVerticalAlignment={splitheaderTextVerticalAlign}
              secondClassName="section-padding-y"
              verticalBreapoints={"tablet"}
              sticky="first"
              stickyTop={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "var(--navbar-height)"
                  : "calc(var(--navbar-height) + var(--section-padding-y))"
              }
              stickyMaxHeight="calc(100dvh - var(--navbar-height))"
              gap={"8.3333%"}
              gapOn="second"
              firstWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: `calc(100% - ${bulmaColumnsToWidth(curr.number_of_cols)})`,
                      };
                    }, {})
              }
              secondWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: bulmaColumnsToWidth(
                          curr.number_of_cols,
                        ),
                      };
                    }, {})
              }
              containerWidthLimit={{
                first: false,
                second: splitheaderImageHeight === "screen-height-frameless",
              }}
            />
          }
        />
      );
  }
};
